import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import type {NutritionTargets} from '../../domain/nutrition-targets.value-object';
import {isLocalDate} from '../../lib/local-date';

const KCAL_MAX_EXCLUSIVE = 20000;
const MACRO_MAX = 2000;
const PLAN_NAME_MAX = 80;

const planDateSchema = z
  .string()
  .refine(isLocalDate, {message: 'Date must be formatted as YYYY-MM-DD.'})
  .nullable();

const nutritionTargetsSchema = z
  .strictObject({
    kcal: z.number().positive().lt(KCAL_MAX_EXCLUSIVE).optional(),
    proteinGrams: z.number().min(0).max(MACRO_MAX).optional(),
    carbsGrams: z.number().min(0).max(MACRO_MAX).optional(),
    fatGrams: z.number().min(0).max(MACRO_MAX).optional(),
  })
  .refine(
    targets =>
      targets.kcal !== undefined ||
      targets.proteinGrams !== undefined ||
      targets.carbsGrams !== undefined ||
      targets.fatGrams !== undefined,
    {message: 'Set at least one target (calories, protein, carbs, or fat).'},
  );

/** Full edit form shape (all fields present); maps to a partial update payload. */
export type EditNutritionPlanFormValues = {
  readonly name: string;
  readonly targets: NutritionTargets;
  readonly startDate: string | null;
  readonly endDate: string | null;
};

export type UpdateNutritionPlanData = {
  readonly name?: string;
  readonly targets?: NutritionTargets;
  readonly startDate?: string | null;
  readonly endDate?: string | null;
};

export class UpdateNutritionPlanDataValidator implements Validator<EditNutritionPlanFormValues> {
  readonly standardSchema = z
    .strictObject({
      name: z.string().trim().min(1, 'Name is required.').max(PLAN_NAME_MAX),
      targets: nutritionTargetsSchema,
      startDate: planDateSchema,
      endDate: planDateSchema,
    })
    .superRefine((value, ctx) => {
      if (value.startDate !== null && value.endDate !== null && value.startDate > value.endDate) {
        ctx.addIssue({
          code: 'custom',
          message: 'End date must be on or after start date.',
          path: ['endDate'],
        });
      }
    });

  validate(value: unknown): Result<EditNutritionPlanFormValues, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);

    if (!parsed.success) {
      return err(
        new ValidationError(
          parsed.error.issues[0]?.message ?? 'Invalid nutrition plan update data.',
        ),
      );
    }

    return ok(parsed.data);
  }
}
